/**
 * The words that mean the same thing on every surface.
 *
 * The API returns stable codes and never prose — that is what lets one backend serve
 * Bulgarian and English without a redeploy — so every user-facing string lives in a
 * client. Some of them are the same string in all of them: an order is `EnRouteToPickup`
 * whether a dispatcher, a driver, or a passenger is reading about it, and translating
 * that three times is three chances to say it three different ways.
 *
 * **Shared and app-specific keys are deliberately disjoint.** This package owns `errors`,
 * `states`, `shiftStates`, `actors`, `fare`, `language`, `theme` and `vehicles` — a car's
 * class and features, said alike by the console and both apps (#369); each app owns
 * everything else. That
 * keeps the merge a shallow spread rather than a deep one — a deep merge would quietly
 * let an app override half of a shared block and leave the other half behind, which is
 * the kind of divergence nobody notices until a driver and a dispatcher describe the same
 * ride differently.
 */

import bg from './locales/bg.json';
import en from './locales/en.json';

export const supportedLanguages = ['bg', 'en'] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number];

/** Translations every surface shares. Merge these under an app's own strings. */
export const shared: Readonly<Record<SupportedLanguage, object>> = { bg, en };

/**
 * Bulgarian, because the first tenants are Bulgarian.
 *
 * A tenant's configured culture overrides it once one is resolved, and a person's own
 * choice overrides that.
 */
export const fallbackLanguage: SupportedLanguage = 'bg';

/**
 * Reduces a culture to a language this app has words for.
 *
 * Tenant cultures are full BCP-47 — `bg-BG`, `en-IE` — while the bundles are keyed by the
 * base language, so the region is dropped rather than failing to match and silently
 * falling back.
 */
export function baseLanguage(culture: string | null | undefined): SupportedLanguage {
  const base = (culture ?? '').split('-')[0]?.toLowerCase();

  return (supportedLanguages as readonly string[]).includes(base ?? '')
    ? (base as SupportedLanguage)
    : fallbackLanguage;
}

/**
 * Builds the resource bundle for one app.
 *
 * @param own The app's own strings, keyed by language.
 */
export function resourcesFor(
  own: Readonly<Record<SupportedLanguage, object>>,
): Record<SupportedLanguage, { translation: object }> {
  return {
    bg: { translation: { ...shared.bg, ...own.bg } },
    en: { translation: { ...shared.en, ...own.en } },
  };
}
